import React, { useEffect, useState } from 'react';
import Button from '../../../../components/Form/Button';
import { InfoModal } from '../../../../components/Modal/InfoModal';
import { Icon, Icons } from 'components/Icon';
import TextAreaFieldWithAutoHeightResize from 'components/Form/TextAreaFieldWithAutoHeightResize';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDeactiveCustomer } from 'common/queries-and-mutations/admin/customer';

type Props = {
  onSuccess: () => void;
  onClose: () => void;
  className?: string;
  customerId: number;
};

export default function DeactivateModal({
  onSuccess,
  onClose,
  className = 'w-[20%]',
  customerId
}: Readonly<Props>) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate, isSuccess, isError } = useDeactiveCustomer();

  const formik = useFormik({
    initialValues: {
      note: ''
    },
    validationSchema: Yup.object({
      note: Yup.string().required(
        'Please enter reason why you want to deactivate this customer'
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      handleDeactivate(values);
    }
  });

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  const handleDeactivate = ({ note }: { note: string }) => {
    mutate({ note, customerId });
  };

  return (
    <InfoModal width={className} className="h-fit rounded-lg py-2">
      <div className="flex w-full flex-col items-center bg-white font-circular text-2xl font-bold text-[#353535]">
        <form onSubmit={formik.handleSubmit} className="w-full">
          <div className="flex items-center justify-between border-b border-[#E5E5E5] p-3 px-5">
            <p className="text-left text-base font-medium">
              Deactivate Customer
            </p>
            <div className="" role="button" onClick={onClose}>
              <Icon name={Icons.Close} />
            </div>
          </div>
          <div className="p-5">
            <div className="group relative z-0 mb-6 mt-3 w-full">
              <TextAreaFieldWithAutoHeightResize
                label="Reason for deactivation"
                name={`note`}
                value={formik.values.note}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder=""
                rows={8}
                error={formik.touched.note ? formik.errors.note : ''}
                className="w-full rounded-2xl border-[1.5px] border-gray-300"
              />
            </div>
          </div>
          <div className="mr-6 flex items-center justify-end gap-4 pb-4">
            <Button
              variant="outline"
              size="md"
              className="mb-3 w-[10rem] rounded px-[1.5rem] py-1 text-sm font-normal text-primary-800"
              label="Cancel"
              onClick={onClose}
            ></Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="mb-3 w-[10rem] rounded text-sm font-normal text-white"
              label="Deactivate"
              loading={isSubmitting}
              disabled={isSubmitting}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
